"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";
import { Suspense, useEffect, useRef, useState } from "react";
import type { FishHabitat } from "@/lib/types";

const ROLES = ["Captain", "Diver", "Aquarist", "Naturalist"] as const;

const HABITATS: { id: FishHabitat; label: string }[] = [
  { id: "reef", label: "Reef" },
  { id: "freshwater", label: "Freshwater" },
  { id: "deep-sea", label: "Deep sea" },
  { id: "coastal", label: "Coastal" },
];

const inputClass =
  "mt-1 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white placeholder:text-teal-200/40 focus:border-teal-400/50 focus:outline-none";
const labelClass =
  "block text-xs font-medium uppercase tracking-wide text-teal-200/80";

function RegisterFormInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const regAvatarRef = useRef<HTMLInputElement>(null);
  const regCoverRef = useRef<HTMLInputElement>(null);
  const [asCaptain, setAsCaptain] = useState(false);

  useEffect(() => {
    if (searchParams.get("captain") === "1") {
      setAsCaptain(true);
    }
  }, [searchParams]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [region, setRegion] = useState("");
  const [role, setRole] = useState<string>("Diver");
  const [schoolPriceUsd, setSchoolPriceUsd] = useState("9");
  const [schoolCount, setSchoolCount] = useState("0");
  const [conservationNote, setConservationNote] = useState("");
  const [habitatReef, setHabitatReef] = useState(true);
  const [habitatFresh, setHabitatFresh] = useState(false);
  const [habitatDeep, setHabitatDeep] = useState(false);
  const [habitatCoastal, setHabitatCoastal] = useState(false);

  function selectedHabitats(): FishHabitat[] {
    const list: FishHabitat[] = [];
    if (habitatReef) list.push("reef");
    if (habitatFresh) list.push("freshwater");
    if (habitatDeep) list.push("deep-sea");
    if (habitatCoastal) list.push("coastal");
    return list;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPending(true);
    setError(null);

    const fd = new FormData();
    fd.append("email", email.trim());
    fd.append("password", password);
    if (name.trim()) {
      fd.append("name", name.trim());
    }
    if (asCaptain) {
      const habitats = selectedHabitats();
      fd.append(
        "captain",
        JSON.stringify({
          slug: slug.trim().toLowerCase(),
          displayName: displayName.trim(),
          bio: bio.trim(),
          region: region.trim(),
          role,
          schoolPriceUsd: Number(schoolPriceUsd),
          schoolCount: Number(schoolCount),
          conservationNote: conservationNote.trim() || null,
          habitats: habitats.length > 0 ? habitats : ["reef"],
        }),
      );
      const av = regAvatarRef.current?.files?.[0];
      if (av) fd.append("avatar", av);
      const cv = regCoverRef.current?.files?.[0];
      if (cv) fd.append("cover", cv);
    }

    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        body: fd,
      });
      const j = (await res.json().catch(() => ({}))) as {
        error?: string;
        slug?: string | null;
      };
      if (!res.ok) {
        setError(j.error ?? "Could not register");
        return;
      }

      const signInRes = await signIn("credentials", {
        email: email.trim(),
        password,
        redirect: false,
      });
      if (signInRes?.error) {
        router.push(
          j.slug
            ? `/auth/signin?registered=1&next=/dashboard/${j.slug}`
            : "/auth/signin?registered=1",
        );
        return;
      }

      if (j.slug) {
        router.push(`/dashboard/${j.slug}`);
      } else {
        router.push("/explore");
      }
      router.refresh();
    } catch {
      setError("Network error");
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={(ev) => void onSubmit(ev)} className="mt-10 space-y-8">
      <div className="rounded-2xl border border-white/10 bg-black/20 p-8 space-y-4">
        <h2 className="text-sm font-semibold text-teal-200">Account</h2>
        <div>
          <label className={labelClass}>Name (optional)</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Email *</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Password * (min 8 characters)</label>
          <input
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
          />
        </div>

        <label className="flex cursor-pointer items-start gap-3 text-sm text-teal-100/90">
          <input
            type="checkbox"
            className="mt-1"
            checked={asCaptain}
            onChange={(e) => setAsCaptain(e.target.checked)}
          />
          <span>
            <strong className="text-white">Create my captain profile</strong> on
            OnlyFish (public page, dashboard, posts). Leave off for a fan-only
            account—you can add a profile later from the dashboard.
          </span>
        </label>
      </div>

      {asCaptain && (
        <div className="rounded-2xl border border-teal-400/20 bg-teal-950/20 p-8 space-y-6">
          <h2 className="text-sm font-semibold text-teal-200">Captain profile</h2>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={labelClass}>URL slug *</label>
              <p className="mt-1 text-xs text-teal-200/60">
                /creators/
                <span className="text-teal-100">{slug || "your-name"}</span>
              </p>
              <input
                value={slug}
                onChange={(e) =>
                  setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))
                }
                required={asCaptain}
                minLength={2}
                className={inputClass}
                placeholder="your-name"
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Display name *</label>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required={asCaptain}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Region *</label>
              <input
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                required={asCaptain}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Role *</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className={inputClass}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Bio *</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                required={asCaptain}
                rows={3}
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2">
              <span className={labelClass}>Habitats</span>
              <div className="mt-2 flex flex-wrap gap-4">
                {HABITATS.map(({ id, label }) => (
                  <label key={id} className="flex items-center gap-2 text-sm text-teal-100">
                    <input
                      type="checkbox"
                      checked={
                        (id === "reef" && habitatReef) ||
                        (id === "freshwater" && habitatFresh) ||
                        (id === "deep-sea" && habitatDeep) ||
                        (id === "coastal" && habitatCoastal)
                      }
                      onChange={(e) => {
                        if (id === "reef") setHabitatReef(e.target.checked);
                        if (id === "freshwater") setHabitatFresh(e.target.checked);
                        if (id === "deep-sea") setHabitatDeep(e.target.checked);
                        if (id === "coastal") setHabitatCoastal(e.target.checked);
                      }}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className={labelClass}>School price (USD/mo)</label>
              <input
                type="number"
                min={0}
                step={0.5}
                value={schoolPriceUsd}
                onChange={(e) => setSchoolPriceUsd(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>School size (demo)</label>
              <input
                type="number"
                min={0}
                value={schoolCount}
                onChange={(e) => setSchoolCount(e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Captain photos (optional)</label>
              <p className="mt-1 text-xs text-teal-200/60">
                Stored in the database. Leave empty for default placeholders; upload or use your
                camera where supported.
              </p>
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <input
                  ref={regAvatarRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="user"
                  className={`${inputClass} py-2 text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-teal-500/30 file:px-3 file:py-1.5 file:text-teal-50`}
                />
                <input
                  ref={regCoverRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  className={`${inputClass} py-2 text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-teal-500/30 file:px-3 file:py-1.5 file:text-teal-50`}
                />
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Conservation note (optional)</label>
              <textarea
                value={conservationNote}
                onChange={(e) => setConservationNote(e.target.value)}
                rows={2}
                className={inputClass}
              />
            </div>
          </div>
        </div>
      )}

      {error && (
        <p className="text-sm text-amber-200/90" role="alert">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={pending}
        className="w-full rounded-xl bg-teal-400 px-4 py-3 font-semibold text-teal-950 hover:bg-teal-300 disabled:opacity-60"
      >
        {pending ? "Working…" : asCaptain ? "Create account & captain profile" : "Create account"}
      </button>
    </form>
  );
}

export function RegisterForm() {
  return (
    <Suspense fallback={<p className="text-teal-200/70">Loading…</p>}>
      <RegisterFormInner />
    </Suspense>
  );
}
